/**
 * Which repository a ticket builds in, when its Team has several. Shared by
 * Add tickets (a picker per picked ticket) and the case file (the "Builds in"
 * line and its picker), so both read the Team's repositories the same way and
 * say the same words.
 */
import { useEffect, useState } from "react";
import type { RepoSuggestion } from "@yagni/shared";
import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import { NativeSelect } from "../ui/native-select.js";
import { captureUiError } from "../sentry.js";
import {
  fetchRepoSuggestion,
  fetchTeamRepos,
  type RepoSuggestionTicket,
  type TeamRepo,
} from "../teams/teamsApi.js";

export type { RepoSuggestion, RepoSuggestionTicket, TeamRepo };

export const REPO_CHOOSE = "Choose a repository";
export const REPO_USE_FOR_ALL = "Use for all";
export const REPO_LABEL = "Repository";
export const REPO_CHOOSE_TO_START = "This Team has several repositories. Choose the one this builds in.";
export function repoBuildsIn(fullName: string): string {
  return `Builds in ${fullName}`;
}
/** The refusal code an approval carries when the ticket's repository was removed. */
export const REPO_REMOVED_CODE = "repo_removed";
export function repoRemoved(fullName: string): string {
  return `${fullName} was removed from the workspace. Choose this ticket's repository again.`;
}

/**
 * A `repos` list off the wire: only entries with an id and a name, with the
 * index's description and language when the server sent them.
 */
export function readRepoList(raw: unknown): TeamRepo[] {
  if (!Array.isArray(raw)) return [];
  const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
  return raw.flatMap((r: unknown) => {
    const entry = r as { id?: unknown; fullName?: unknown; description?: unknown; language?: unknown } | null;
    if (typeof entry?.id !== "string" || typeof entry.fullName !== "string") return [];
    return [
      {
        id: entry.id,
        fullName: entry.fullName,
        description: text(entry.description),
        language: text(entry.language),
      },
    ];
  });
}

/** The refusal code off an error body, whichever key the route used. */
export function refusalCode(body: { error?: unknown; code?: unknown } | null | undefined): string | null {
  if (typeof body?.code === "string") return body.code;
  if (typeof body?.error === "string") return body.error;
  return null;
}

/**
 * The Team's buildable repositories; null until the read answers, and on a
 * failed read (the pages then fall back to what the server offers on a
 * refusal).
 */
export function useTeamRepos(deskId: string | null | undefined): TeamRepo[] | null {
  return useTeamReposState(deskId).repos;
}

/**
 * The Team's repositories with the state of the read: `repos` is null while
 * reading and after a failed read, `failed` says which, and `retry` reads
 * again.
 */
export function useTeamReposState(deskId: string | null | undefined): {
  repos: TeamRepo[] | null;
  failed: boolean;
  retry: () => void;
} {
  const [repos, setRepos] = useState<TeamRepo[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setRepos(null);
    setFailed(false);
    if (!deskId) return;
    let cancelled = false;
    void fetchTeamRepos(deskId)
      .then((list) => {
        if (!cancelled) setRepos(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        captureUiError(err, { area: "Work", operation: "team_repos_read", deskId });
        setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [deskId, attempt]);
  return { repos, failed, retry: () => setAttempt((n) => n + 1) };
}

/** What a PATCH that set the repository did about planning, when it said. */
export interface RepoPlanning {
  status: "started" | "not_started";
  reason?: string;
}

export type RepoPatchResult =
  /** `planning` is null from a backend that does not start planning on the PATCH. */
  | { ok: true; planning: RepoPlanning | null }
  | { ok: false; code: string | null; message: string; repos: TeamRepo[] };

function readPlanning(raw: unknown): RepoPlanning | null {
  if (!raw || typeof raw !== "object") return null;
  const entry = raw as { status?: unknown; reason?: unknown };
  if (entry.status !== "started" && entry.status !== "not_started") return null;
  return {
    status: entry.status,
    ...(typeof entry.reason === "string" && entry.reason.trim() ? { reason: entry.reason.trim() } : {}),
  };
}

/**
 * `PATCH /api/work-items/:id { repoId }`. A refusal comes back as words:
 * `repo_locked` once a run holds the ticket or it has a pull request,
 * `repo_not_on_team` (with the Team's list) when the repository left it.
 */
export async function patchWorkItemRepo(workItemId: string, repoId: string): Promise<RepoPatchResult> {
  try {
    const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ repoId }),
    });
    if (res.ok) {
      const updated = (await res.json().catch(() => null)) as { planning?: unknown } | null;
      return { ok: true, planning: readPlanning(updated?.planning) };
    }
    const body = (await res.json().catch(() => null)) as
      | { error?: unknown; code?: unknown; message?: unknown; repos?: unknown }
      | null;
    const words =
      typeof body?.message === "string" && body.message.trim()
        ? body.message.trim()
        : typeof body?.error === "string" && body.error.trim()
          ? body.error.trim()
          : REPO_PATCH_FAILED;
    return { ok: false, code: typeof body?.code === "string" ? body.code : null, message: words, repos: readRepoList(body?.repos) };
  } catch {
    return { ok: false, code: null, message: REPO_PATCH_FAILED, repos: [] };
  }
}

export const REPO_PATCH_FAILED = "Could not set the repository. Try again.";

/**
 * The case file's "Builds in owner/name" line. When the Team has several
 * repositories and the ticket can still move, it is a picker; a refusal
 * (a run already holds the ticket) is said quietly under it. A ticket whose
 * repository was removed from the workspace (`removedRepoRef`) says so and
 * offers the picker, even on a Team with one repository.
 */
export function RepoLine({
  workItemId,
  repoId,
  repos,
  canChange,
  onChanged,
  noMatchReason,
  suggestion,
  removedRepoRef,
}: {
  workItemId: string;
  repoId: string | null;
  repos: readonly TeamRepo[] | null;
  canChange: boolean;
  onChanged: (repoId: string) => void;
  /** `owner/name` of the ticket's repository while it is removed from the workspace. */
  removedRepoRef?: string | null;
  /** The Team's `repo.route` suggestion for this ticket, while it has no repository. */
  suggestion?: RepoSuggestion | null;
  /**
   * Why the Team's proposer named no repository for this bet (YAG-831,
   * `repoReason` on its evidence). Said above the picker while it has none.
   */
  noMatchReason?: string | null;
}) {
  const [chosen, setChosen] = useState<string | null>(repoId);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    setChosen(repoId);
    setNote(null);
  }, [workItemId, repoId]);

  const list = repos ?? [];
  const current = list.find((repo) => repo.id === chosen) ?? null;
  const removed = !current && removedRepoRef?.trim() ? removedRepoRef.trim() : null;
  const picker = canChange && (list.length > 1 || (removed !== null && list.length > 0));
  const reason = !current && !removed && noMatchReason?.trim() ? noMatchReason.trim() : null;
  if (!picker && !current && !reason && !removed) return null;

  const pick = async (next: string) => {
    if (!next || next === chosen) return;
    setBusy(true);
    setNote(null);
    const result = await patchWorkItemRepo(workItemId, next);
    setBusy(false);
    if (result.ok) {
      setChosen(next);
      onChanged(next);
      return;
    }
    setNote(result.message);
  };

  return (
    <div className="cf-signals-line cf-repo-line" data-testid="case-file-repo">
      {reason && (
        <span className="cf-repo-note basis-full" data-testid="case-file-repo-no-match">
          {repoNoMatch(reason)}
        </span>
      )}
      {removed && (
        <span className="cf-repo-note basis-full" data-testid="case-file-repo-removed">
          {repoRemoved(removed)}
        </span>
      )}
      {picker ? (
        <label className="cf-repo-pick">
          <span>Builds in</span>
          <RepoSelect
            repos={list}
            value={chosen ?? ""}
            disabled={busy}
            label={REPO_LABEL}
            testId="case-file-repo-select"
            // The proposer's own no-match reason is already said above; a
            // second "No repository matches" under the picker would repeat it.
            suggestion={reason && !suggestion?.repoId ? null : suggestion}
            confirmSuggestion
            onChange={(next) => void pick(next)}
          />
        </label>
      ) : (
        current && <span>{repoBuildsIn(current.fullName)}</span>
      )}
      {note && (
        <span className="cf-repo-note" role="status" data-testid="case-file-repo-note">
          {note}
        </span>
      )}
    </div>
  );
}

/** Above this many repositories, the picker grows a search box. */
export const REPO_SEARCH_THRESHOLD = 8;
export const REPO_SEARCH_PLACEHOLDER = "Search repositories";
export function repoNoMatch(reason: string): string {
  return `No repository matches: ${reason}`;
}

export function repoSuggested(reason: string): string {
  return reason ? `Suggested: ${reason}` : "Suggested";
}
export const REPO_USE_SUGGESTED = "Use suggested";

/**
 * The repository the Team's `repo.route` suggests for a ticket. Null until it
 * answers, when there is nothing to ask about, and on any failure.
 */
export function useRepoSuggestion(
  deskId: string | null | undefined,
  ticket: RepoSuggestionTicket | null,
): RepoSuggestion | null {
  const [suggestion, setSuggestion] = useState<RepoSuggestion | null>(null);
  const title = ticket?.title.trim() ?? "";
  const body = ticket?.body ?? "";
  const labels = ticket?.labels?.join(",") ?? "";
  const components = ticket?.components?.join(",") ?? "";
  const trackerKey = ticket?.trackerKey ?? "";
  useEffect(() => {
    setSuggestion(null);
    if (!deskId || !title) return;
    let cancelled = false;
    void fetchRepoSuggestion(deskId, {
      title,
      body,
      trackerKey,
      labels: labels ? labels.split(",") : [],
      components: components ? components.split(",") : [],
    }).then((answer) => {
      if (!cancelled) setSuggestion(answer);
    });
    return () => {
      cancelled = true;
    };
  }, [deskId, title, body, labels, components, trackerKey]);
  return suggestion;
}

/**
 * The repository picker. With a `suggestion` and no value, the suggested
 * repository shows as preselected with "Suggested: reason" beneath; an honest
 * no-match says "No repository matches: reason" and leaves the select empty.
 * A parent whose `onChange` commits at once (the gate, the case file) passes
 * `confirmSuggestion`, so the preselection takes a click before it counts.
 */
export function RepoSelect({
  repos,
  value,
  onChange,
  disabled,
  label,
  testId,
  suggestion,
  confirmSuggestion,
}: {
  repos: readonly TeamRepo[];
  value: string;
  onChange: (repoId: string) => void;
  disabled?: boolean;
  label: string;
  testId: string;
  suggestion?: RepoSuggestion | null;
  confirmSuggestion?: boolean;
}) {
  const [query, setQuery] = useState("");
  const searchable = repos.length > REPO_SEARCH_THRESHOLD;
  const wanted = searchable ? query.trim().toLowerCase() : "";
  const suggestedId =
    suggestion?.repoId && repos.some((repo) => repo.id === suggestion.repoId) ? suggestion.repoId : null;
  const preselected = !value && suggestedId !== null;
  const shownValue = preselected ? suggestedId : value;
  // The chosen repository always keeps its option, so a filter never blanks
  // what the select shows.
  const shown = wanted
    ? repos.filter((repo) => repo.id === shownValue || repo.fullName.toLowerCase().includes(wanted))
    : repos;
  const selected = repos.find((repo) => repo.id === shownValue) ?? null;
  const suggestedShown = suggestedId !== null && shownValue === suggestedId;
  const noMatch = !shownValue && suggestion && !suggestion.repoId && suggestion.reason ? suggestion.reason : null;
  return (
    <span className="inline-flex flex-col gap-xs">
      {searchable && (
        <Input
          type="search"
          inputSize="sm"
          value={query}
          disabled={disabled}
          placeholder={REPO_SEARCH_PLACEHOLDER}
          aria-label={`${label}: search`}
          data-testid={`${testId}-search`}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
      <NativeSelect
        selectSize="sm"
        value={shownValue}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      >
        <option value="">{REPO_CHOOSE}</option>
        {shown.map((repo) => (
          <option key={repo.id} value={repo.id}>
            {repo.fullName}
          </option>
        ))}
      </NativeSelect>
      {selected?.description && (
        <span className="text-xs text-text-tertiary" data-testid={`${testId}-description`}>
          {selected.description}
        </span>
      )}
      {suggestedShown && (
        <span className="text-xs text-text-secondary" data-testid={`${testId}-suggested`}>
          {repoSuggested(suggestion?.reason ?? "")}
        </span>
      )}
      {preselected && confirmSuggestion && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => {
            if (suggestedId) onChange(suggestedId);
          }}
          data-testid={`${testId}-use-suggested`}
        >
          {REPO_USE_SUGGESTED}
        </Button>
      )}
      {noMatch && (
        <span className="text-xs text-text-tertiary" data-testid={`${testId}-no-match`}>
          {repoNoMatch(noMatch)}
        </span>
      )}
    </span>
  );
}
